import React, { useMemo, useState } from 'react';
import { AlertTriangle, AlertOctagon, Eye, XCircle, CheckCircle2, Zap, TrendingUp, FileText, ShieldCheck, ArrowRight, ChevronDown, ChevronRight, Info, Database, Shield, VolumeX, Filter } from 'lucide-react';
import { StatCard } from '../../../../shared/StatCard';
import { Badge } from '../../../../../components/ui/Badge';
import { Card } from '../../../../../components/ui/Card';
import { MODULE_CONFIG, PIPELINE_STAGES } from '../shared';
import { SeedTriageCard } from './SeedTriageCard';
import type { GovernanceStats, ModuleHealth, FamilyHealth, FamilyTriageItem, UnifiedQuestionRow, SystemStats } from '../hooks/useGovernanceData';
import type { IssueDomain, CodeFrequencyReport } from '../../../../../governance';
import { getGuardGovernanceIssues } from '../../../../../practice/logic/guardGovernanceBridge';
interface OverviewDashboardProps {
  stats: GovernanceStats;
  systemStats: SystemStats;
  moduleHealth: ModuleHealth[];
  familyHealthByModule: Record<string, FamilyHealth[]>;
  questionTypeDistribution: Map<string, number>;
  visualTypeDistribution: Map<string, number>;
  topBlockers: UnifiedQuestionRow[];
  familyTriage: FamilyTriageItem[];
  pipelineIssueCounts: Record<IssueDomain, number> | null;
  codeFrequencyReport?: CodeFrequencyReport | null;
  isRunning: boolean;
  onNavigateToQuestion: (filter?: string) => void;
}
export function OverviewDashboard({
  stats,
  systemStats,
  moduleHealth,
  familyHealthByModule,
  questionTypeDistribution,
  visualTypeDistribution,
  topBlockers,
  familyTriage,
  pipelineIssueCounts,
  codeFrequencyReport,
  isRunning,
  onNavigateToQuestion
}: OverviewDashboardProps) {
  const isHealthy = stats.fail === 0 && stats.review === 0;
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [hideInfoCodes, setHideInfoCodes] = useState(false);
  const [hideNoiseCodes, setHideNoiseCodes] = useState(false);
  // P-9: Guard → Governance feedback data
  const guardSummary = useMemo(() => getGuardGovernanceIssues(), []);
  const toggleModule = (code: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);else next.add(code);
      return next;
    });
  };
  // Total findings across all pipeline stages
  const totalFindings = pipelineIssueCounts ? Object.values(pipelineIssueCounts).reduce((sum, c) => sum + c, 0) : 0;
  // Filtered top codes for the noise widget (computed when report present)
  const filteredTopCodes = useMemo(() => {
    if (!codeFrequencyReport) return [];
    let list = codeFrequencyReport.entries;
    if (hideInfoCodes) list = list.filter(e => e.kind !== 'info');
    if (hideNoiseCodes) list = list.filter(e => !e.suspectedNoise);
    return list.slice(0, 10);
  }, [codeFrequencyReport, hideInfoCodes, hideNoiseCodes]);
  return <div className="space-y-6" data-id="element-4346">
      {/* Health Banner — question-level summary */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isHealthy ? 'bg-emerald-50 border-emerald-200' : stats.fail > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-4347">
        {isHealthy ? <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-4348" /> : stats.fail > 0 ? <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-4349" /> : <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" data-id="element-4350" />}
        <div className="flex-1 min-w-0" data-id="element-4351">
          <span className={`text-sm font-semibold ${isHealthy ? 'text-emerald-800' : stats.fail > 0 ? 'text-red-800' : 'text-amber-800'}`} data-id="element-4352">
            {isHealthy ? 'Alle vragen voldoen aan de kwaliteitseisen' : stats.fail > 0 ? `${stats.fail} ${stats.fail === 1 ? 'vraag' : 'vragen'} afgekeurd` : 'Aandachtspunten gevonden'}
          </span>
          {!isHealthy && <span className={`text-xs ml-2 ${stats.fail > 0 ? 'text-red-700' : 'text-amber-700'}`} data-id="element-4353">
              {[stats.review > 0 && `${stats.review} vereisen review`, totalFindings > 0 && `${totalFindings} bevindingen totaal`].filter(Boolean).join(' · ')}
            </span>}
        </div>
      </div>

      {/* Stats — question-level counts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4354">
        <StatCard label="Totaal Vragen" value={stats.total.toLocaleString('nl-NL')} icon={<FileText className="w-5 h-5" data-id="element-4356" />} color="blue" subtitle="Unieke vragen in het systeem" data-id="element-4355" />
        <StatCard label="Geslaagd (PASS)" value={stats.pass.toLocaleString('nl-NL')} icon={<CheckCircle2 className="w-5 h-5" data-id="element-4358" />} color="emerald" subtitle={`${Math.round(stats.pass / Math.max(stats.total, 1) * 100)}% van alle vragen`} data-id="element-4357" />
        <StatCard label="Afgekeurd (FAIL)" value={stats.fail.toLocaleString('nl-NL')} icon={<XCircle className="w-5 h-5" data-id="element-4360" />} color={stats.fail === 0 ? 'emerald' : 'red'} subtitle={`${stats.blockerCount} blocker-bevindingen`} data-id="element-4359" />
        <StatCard label="Review Nodig" value={stats.review.toLocaleString('nl-NL')} icon={<AlertTriangle className="w-5 h-5" data-id="element-4362" />} color={stats.review === 0 ? 'emerald' : 'amber'} subtitle={`${stats.warningCount} verbeterpunten`} data-id="element-4361" />
      </div>

      {/* Phase 1: 5-level severity tiles — display-only breakdown of all issues
                      across all questions. The 4 verdict tiles above count QUESTIONS;
                      these 5 tiles count ISSUES by display-severity. */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4" data-id="element-4363">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-4364">
          Bevindingen per ernst
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-id="element-4365">
          {[{
          key: 'critical' as const,
          label: 'Kritiek',
          Icon: AlertOctagon,
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700',
          iconClass: 'text-red-600',
          hint: 'Blocker · ernst ≥90'
        }, {
          key: 'blocker' as const,
          label: 'Blocker',
          Icon: XCircle,
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          text: 'text-orange-700',
          iconClass: 'text-orange-600',
          hint: 'Blocker · ernst 70-89'
        }, {
          key: 'review' as const,
          label: 'Review',
          Icon: Eye,
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-700',
          iconClass: 'text-yellow-600',
          hint: 'Coach · ernst ≥45'
        }, {
          key: 'coach' as const,
          label: 'Coach',
          Icon: AlertTriangle,
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-700',
          iconClass: 'text-blue-600',
          hint: 'Coach · ernst <45'
        }, {
          key: 'info' as const,
          label: 'Info',
          Icon: Info,
          bg: 'bg-slate-50',
          border: 'border-slate-200',
          text: 'text-slate-700',
          iconClass: 'text-slate-500',
          hint: 'Observatie'
        }].map(tier => {
          const count = stats.severityCounts?.[tier.key] ?? 0;
          const TierIcon = tier.Icon;
          return <div key={tier.key} className={`flex items-center gap-2 p-2.5 rounded-lg border ${tier.bg} ${tier.border}`} title={tier.hint} data-id="element-4366">
                <TierIcon className={`w-4 h-4 flex-shrink-0 ${tier.iconClass}`} data-id="element-4367" />
                <div className="min-w-0 flex-1" data-id="element-4368">
                  <p className={`text-lg font-bold leading-none ${tier.text}`} data-id="element-4369">
                    {count}
                  </p>
                  <p className={`text-[10px] font-medium ${tier.text} mt-0.5`} data-id="element-4370">
                    {tier.label}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* Explainer legend */}
      <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-blue-50/50 border border-blue-100" data-id="element-4371">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-4372" />
        <div className="text-xs text-blue-800 leading-relaxed" data-id="element-4373">
          <span className="font-semibold" data-id="element-4374">Hoe lees je deze cijfers?</span>{' '}
          <span className="font-semibold text-red-700" data-id="element-4375">Afgekeurd</span> en{' '}
          <span className="font-semibold text-amber-700" data-id="element-4376">Review</span> tellen
          het aantal <em data-id="element-4377">vragen</em> met die status. Elke vraag kan meerdere{' '}
          <em data-id="element-4378">bevindingen</em> hebben (Kritiek/Blocker/Review/Coach/Info). De
          Pipeline Fasen hieronder tonen het totaal aantal bevindingen per
          controle-fase.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4379">
        {/* Left Column: Seed Triage (NEW, top priority) → System Coverage → Top Blockers → Pipeline Stages */}
        <div className="lg:col-span-2 space-y-6" data-id="element-4380">
          {/* Seed Prioritizer — answers "which seed file to open first?" */}
          <SeedTriageCard familyTriage={familyTriage} onSelectFamily={familyId => onNavigateToQuestion(familyId)} data-id="element-4381" />

          {/* System Coverage */}
          <Card className="p-5" data-id="element-4382">
            <div className="mb-4" data-id="element-4383">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4384">
                <Database className="w-4 h-4 text-indigo-500" data-id="element-4385" /> Systeemdekking
              </h3>
              <p className="text-[11px] text-gray-500 mt-1" data-id="element-4386">
                Overzicht van beschikbare componenten en hun gebruik in het
                vragenbestand.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-id="element-4387">
              <div className="bg-indigo-50/50 rounded-lg p-3 border border-indigo-100 text-center" data-id="element-4388">
                <p className="text-2xl font-bold text-indigo-700" data-id="element-4389">
                  {systemStats.visualTypeCount}
                </p>
                <p className="text-xs font-medium text-indigo-600 mt-1" data-id="element-4390">
                  Visual Types
                </p>
              </div>
              <div className="bg-purple-50/50 rounded-lg p-3 border border-purple-100 text-center" data-id="element-4391">
                <p className="text-2xl font-bold text-purple-700" data-id="element-4392">
                  {systemStats.questionTypeCount}
                </p>
                <p className="text-xs font-medium text-purple-600 mt-1" data-id="element-4393">
                  Vraagtypes
                </p>
              </div>
              <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100 text-center" data-id="element-4394">
                <p className="text-2xl font-bold text-blue-700" data-id="element-4395">
                  {systemStats.familyCount}
                </p>
                <p className="text-xs font-medium text-blue-600 mt-1" data-id="element-4396">
                  Families
                </p>
              </div>
              <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100 text-center" data-id="element-4397">
                <p className="text-2xl font-bold text-emerald-700" data-id="element-4398">
                  {systemStats.moduleCount}
                </p>
                <p className="text-xs font-medium text-emerald-600 mt-1" data-id="element-4399">
                  Modules
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-4400">
              <div data-id="element-4401">
                <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider" data-id="element-4402">
                  Top 5 Vraagtypes
                </h4>
                <div className="space-y-2" data-id="element-4403">
                  {Array.from(questionTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm" data-id="element-4404">
                        <span className="text-gray-600 font-mono text-xs truncate mr-2" data-id="element-4405">
                          {type}
                        </span>
                        <span className="font-medium text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-xs" data-id="element-4406">
                          {count}
                        </span>
                      </div>)}
                </div>
              </div>
              <div data-id="element-4407">
                <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider" data-id="element-4408">
                  Top 5 Visuals
                </h4>
                <div className="space-y-2" data-id="element-4409">
                  {Array.from(visualTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm" data-id="element-4410">
                        <span className="text-gray-600 font-mono text-xs truncate mr-2" data-id="element-4411">
                          {type}
                        </span>
                        <span className="font-medium text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-xs" data-id="element-4412">
                          {count}
                        </span>
                      </div>)}
                </div>
              </div>
            </div>
          </Card>

          {/* Top Blockers */}
          <Card className="p-5" data-id="element-4413">
            <div className="flex items-center justify-between mb-4" data-id="element-4414">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4415">
                <AlertTriangle className="w-4 h-4 text-red-500" data-id="element-4416" /> Afgekeurde
                Vragen
                {topBlockers.length > 0 && <span className="text-xs font-normal text-gray-500" data-id="element-4417">
                    (top {topBlockers.length})
                  </span>}
              </h3>
              {topBlockers.length > 0 && <button onClick={() => onNavigateToQuestion('blockers')} className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-4418">
                  Bekijk alle {stats.fail} <ArrowRight className="w-3 h-3" data-id="element-4419" />
                </button>}
            </div>

            {topBlockers.length === 0 ? <div className="bg-gray-50 rounded-lg p-8 text-center border border-gray-100" data-id="element-4420">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-4421" />
                <p className="text-sm font-medium text-gray-700" data-id="element-4422">
                  Geen afgekeurde vragen!
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-4423">
                  Alle vragen komen door de pipeline.
                </p>
              </div> : <div className="space-y-2" data-id="element-4424">
                {topBlockers.map(row => {
              const firstBlocker = row.pipelineIssues.find(i => i.kind === 'blocker');
              const modConfig = MODULE_CONFIG[row.moduleCode] || {
                bgClass: 'bg-gray-500'
              };
              return <div key={row.questionId} className="flex items-start gap-3 p-3 rounded-lg border border-red-100 bg-red-50/50 hover:bg-red-50 transition-colors cursor-pointer" onClick={() => onNavigateToQuestion(row.questionId)} data-id="element-4425">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${modConfig.bgClass}`} data-id="element-4426" />
                      <div className="min-w-0 flex-1" data-id="element-4427">
                        <div className="flex items-center gap-2 mb-1" data-id="element-4428">
                          <span className="font-mono text-xs font-bold text-gray-900" data-id="element-4429">
                            {row.questionId}
                          </span>
                          <Badge variant="danger" size="sm" data-id="element-4430">
                            FAIL
                          </Badge>
                          <span className="text-[10px] text-gray-500" data-id="element-4431">
                            {row.blockerCount} blocker
                            {row.blockerCount !== 1 ? 's' : ''}
                            {row.warningCount > 0 && ` · ${row.warningCount} verbeterpunt${row.warningCount !== 1 ? 'en' : ''}`}
                          </span>
                        </div>
                        {firstBlocker && <p className="text-sm text-red-800 truncate" data-id="element-4432">
                            <span className="font-semibold text-xs uppercase mr-1" data-id="element-4433">
                              [{firstBlocker.domain}]
                            </span>
                            {firstBlocker.message}
                          </p>}
                      </div>
                    </div>;
            })}
              </div>}
          </Card>

          {/* Pipeline Stages — individual findings per stage */}
          <Card className="p-5" data-id="element-4434">
            <div className="mb-4" data-id="element-4435">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4436">
                <Zap className="w-4 h-4 text-amber-500" data-id="element-4437" /> Pipeline Fasen
              </h3>
              <p className="text-[11px] text-gray-500 mt-1" data-id="element-4438">
                Aantal individuele bevindingen per controle-fase (één vraag kan
                meerdere bevindingen opleveren).
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4439">
              {PIPELINE_STAGES.map(stage => {
              const count = pipelineIssueCounts?.[stage.domain] ?? 0;
              const status = !pipelineIssueCounts ? 'pending' : count === 0 ? 'ok' : count <= 5 ? 'warning' : 'error';
              const statusConfig = {
                ok: {
                  dot: 'bg-emerald-500',
                  bg: 'bg-emerald-50'
                },
                warning: {
                  dot: 'bg-amber-500',
                  bg: 'bg-amber-50'
                },
                error: {
                  dot: 'bg-red-500',
                  bg: 'bg-red-50'
                },
                pending: {
                  dot: 'bg-gray-300',
                  bg: 'bg-gray-50'
                }
              }[status];
              return <div key={stage.name} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50/50" data-id="element-4440">
                    <div className="flex items-center gap-3" data-id="element-4441">
                      <div className={`w-2 h-2 rounded-full ${statusConfig.dot}`} data-id="element-4442" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-4443">
                        {stage.label}
                      </span>
                    </div>
                    {status !== 'pending' && <Badge variant={status === 'ok' ? 'success' : status === 'warning' ? 'warning' : 'danger'} size="sm" data-id="element-4444">
                        {count === 0 ? '0 bevindingen' : `${count} bevindingen`}
                      </Badge>}
                  </div>;
            })}
            </div>
          </Card>

          {/* NEW: Top Noise Sources — diagnose which codes dominate the findings count */}
          {codeFrequencyReport && codeFrequencyReport.totalIssues > 0 && <Card className="p-5" data-id="element-4445">
              <div className="flex items-start justify-between mb-3 gap-4" data-id="element-4446">
                <div className="min-w-0" data-id="element-4447">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4448">
                    <VolumeX className="w-4 h-4 text-purple-500" data-id="element-4449" /> Top
                    Ruisbronnen
                  </h3>
                  <p className="text-[11px] text-gray-500 mt-1" data-id="element-4450">
                    Welke validator-codes domineren de{' '}
                    <span className="font-semibold" data-id="element-4451">
                      {codeFrequencyReport.totalIssues.toLocaleString('nl-NL')}
                    </span>{' '}
                    bevindingen?{' '}
                    <span className="font-semibold" data-id="element-4452">
                      {codeFrequencyReport.uniqueCodes}
                    </span>{' '}
                    unieke codes in deze run.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-4453">
                  <button onClick={() => setHideInfoCodes(v => !v)} className={`text-[10px] font-medium px-2 py-1 rounded border transition-colors ${hideInfoCodes ? 'bg-slate-100 border-slate-300 text-slate-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} title="Verberg info-bevindingen (severity ≤ TRACE)" data-id="element-4454">
                    <Filter className="w-3 h-3 inline mr-1" data-id="element-4455" />
                    {hideInfoCodes ? 'Info verborgen' : 'Verberg info'}
                  </button>
                  <button onClick={() => setHideNoiseCodes(v => !v)} className={`text-[10px] font-medium px-2 py-1 rounded border transition-colors ${hideNoiseCodes ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} title="Verberg codes met verdacht-ruis vlag (>20% van vragen of TRACE-severity)" data-id="element-4456">
                    <VolumeX className="w-3 h-3 inline mr-1" data-id="element-4457" />
                    {hideNoiseCodes ? 'Ruis verborgen' : 'Verberg ruis'}
                  </button>
                </div>
              </div>

              {filteredTopCodes.length === 0 ? <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 text-center" data-id="element-4458">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-1" data-id="element-4459" />
                  <p className="text-xs text-emerald-700 font-medium" data-id="element-4460">
                    Geen codes om weer te geven met huidige filters.
                  </p>
                </div> : <div className="space-y-1.5" data-id="element-4461">
                  {filteredTopCodes.map((entry, idx) => {
              const kindColor = entry.kind === 'blocker' ? 'danger' : entry.kind === 'coach' ? 'warning' : 'neutral';
              return <div key={entry.code} className={`flex items-center gap-3 p-2.5 rounded-lg border ${entry.suspectedNoise ? 'bg-amber-50/40 border-amber-100' : 'bg-gray-50/60 border-gray-100'}`} data-id="element-4462">
                        <span className="text-[10px] font-bold text-gray-400 w-5 flex-shrink-0 text-center" data-id="element-4463">
                          {idx + 1}
                        </span>
                        <div className="min-w-0 flex-1" data-id="element-4464">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-4465">
                            <span className="font-mono text-[11px] font-semibold text-gray-900 truncate" title={entry.code} data-id="element-4466">
                              {entry.code}
                            </span>
                            <Badge variant={kindColor} size="sm" data-id="element-4467">
                              {entry.kind}
                            </Badge>
                            <span className="text-[10px] text-gray-500 font-mono" data-id="element-4468">
                              [{entry.domain}]
                            </span>
                            {entry.suspectedNoise && <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title="Verdacht ruis — komt op >20% van vragen voor of severity ≤ TRACE" data-id="element-4469">
                                ruis?
                              </span>}
                          </div>
                          <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-4470">
                            {entry.questionsAffected.toLocaleString('nl-NL')}{' '}
                            vragen · {entry.percentOfQuestions}% · max ernst{' '}
                            {entry.maxSeverity} · gem. {entry.avgSeverity}
                          </p>
                          {entry.reasonBreakdown && entry.reasonBreakdown.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1" title="Rollup-code — opgesplitst per metadata.reason" data-id="element-4471">
                                {entry.reasonBreakdown.map(r => <span key={r.reason} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-gray-200 text-[10px] text-gray-700 font-mono" data-id="element-4472">
                                    <span className="text-gray-500" data-id="element-4473">
                                      {r.reason}
                                    </span>
                                    <span className="font-semibold text-gray-900" data-id="element-4474">
                                      {r.count.toLocaleString('nl-NL')}
                                    </span>
                                    <span className="text-gray-400" data-id="element-4475">
                                      ({r.percentOfCode}%)
                                    </span>
                                  </span>)}
                              </div>}
                        </div>
                        <div className="flex flex-col items-end flex-shrink-0" data-id="element-4476">
                          <span className="text-base font-bold text-gray-900 leading-none" data-id="element-4477">
                            {entry.count.toLocaleString('nl-NL')}
                          </span>
                          <span className="text-[10px] text-gray-500 mt-0.5" data-id="element-4478">
                            {entry.percentOfTotal}%
                          </span>
                        </div>
                      </div>;
            })}
                </div>}

              {codeFrequencyReport.suspectedNoise.length > 0 && <div className="mt-3 pt-3 border-t border-gray-100 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50/40 -mx-2 px-3 py-2 rounded-lg" data-id="element-4479">
                  <Info className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-4480" />
                  <span data-id="element-4481">
                    <span className="font-semibold" data-id="element-4482">
                      {codeFrequencyReport.suspectedNoise.length}
                    </span>{' '}
                    codes gemarkeerd als <em data-id="element-4483">verdacht ruis</em> — samen goed
                    voor{' '}
                    <span className="font-semibold" data-id="element-4484">
                      {codeFrequencyReport.suspectedNoise.reduce((s, e) => s + e.count, 0).toLocaleString('nl-NL')}
                    </span>{' '}
                    van{' '}
                    {codeFrequencyReport.totalIssues.toLocaleString('nl-NL')}{' '}
                    bevindingen. Overweeg severity te verlagen of als info-emit
                    te markeren als ze niet actiestappen opleveren.
                  </span>
                </div>}
            </Card>}
        </div>

        {/* Right Column: Module Health */}
        <div className="space-y-6" data-id="element-4485">
          <Card className="p-5" data-id="element-4486">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-4487">
              <FileText className="w-4 h-4 text-blue-500" data-id="element-4488" /> Module Gezondheid
            </h3>
            <div className="space-y-3" data-id="element-4489">
              {moduleHealth.map(mod => {
              const config = MODULE_CONFIG[mod.code];
              const bgClass = config?.bgClass ?? 'bg-gray-400';
              const isExpanded = expandedModules.has(mod.code);
              const families = familyHealthByModule[mod.code] || [];
              return <div key={mod.code} className="space-y-1.5 border border-gray-100 rounded-lg p-2 bg-white" data-id="element-4490">
                    <div className="flex items-center justify-between text-xs cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors" onClick={() => toggleModule(mod.code)} data-id="element-4491">
                      <div className="flex items-center gap-1.5 font-medium text-gray-700" data-id="element-4492">
                        {isExpanded ? <ChevronDown className="w-3 h-3 text-gray-400" data-id="element-4493" /> : <ChevronRight className="w-3 h-3 text-gray-400" data-id="element-4494" />}
                        <div className={`w-1.5 h-1.5 rounded-full ${bgClass}`} data-id="element-4495" />
                        {mod.code}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-4496">
                        <span className="text-gray-500" data-id="element-4497">{mod.total} vr</span>
                        <span className={`font-bold ${mod.pct === 100 ? 'text-emerald-600' : mod.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-4498">
                          {mod.pct}%
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mx-1" data-id="element-4499">
                      <div className={`h-full rounded-full ${mod.pct === 100 ? 'bg-emerald-500' : mod.pct >= 80 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${mod.pct}%`
                  }} data-id="element-4500" />
                    </div>

                    {isExpanded && families.length > 0 && <div className="mt-2 pt-2 border-t border-gray-100 space-y-2 pl-4 pr-1" data-id="element-4501">
                        {families.map(fam => <div key={fam.familyId} className="space-y-1" data-id="element-4502">
                            <div className="flex items-center justify-between text-[10px]" data-id="element-4503">
                              <span className="font-mono text-gray-600 truncate mr-2" title={fam.familyId} data-id="element-4504">
                                {fam.familyId}
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0" data-id="element-4505">
                                <span className="text-gray-400" data-id="element-4506">
                                  {fam.pass}/{fam.total}
                                </span>
                                <span className={`font-semibold ${fam.pct === 100 ? 'text-emerald-600' : fam.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-4507">
                                  {fam.pct}%
                                </span>
                              </div>
                            </div>
                            <div className="h-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-4508">
                              <div className={`h-full rounded-full ${fam.pct === 100 ? 'bg-emerald-400' : fam.pct >= 80 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                        width: `${fam.pct}%`
                      }} data-id="element-4509" />
                            </div>
                          </div>)}
                      </div>}
                  </div>;
            })}
            </div>
          </Card>

          {/* P-9: Guard Runtime Rejections */}
          <Card className="p-5" data-id="element-4510">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2" data-id="element-4511">
              <Shield className="w-4 h-4 text-orange-500" data-id="element-4512" /> Runtime Guard
            </h3>
            <p className="text-[11px] text-gray-500 mb-3" data-id="element-4513">
              Vragen die door de pedagogische guard worden afgewezen tijdens
              oefensessies.
            </p>
            {guardSummary.totalRejections === 0 ? <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-100" data-id="element-4514">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4515" />
                <p className="text-xs text-gray-600" data-id="element-4516">
                  Geen runtime afwijzingen
                </p>
              </div> : <div className="space-y-3" data-id="element-4517">
                <div className="grid grid-cols-2 gap-2" data-id="element-4518">
                  <div className="bg-red-50 rounded-lg p-2.5 border border-red-100 text-center" data-id="element-4519">
                    <p className="text-lg font-bold text-red-700" data-id="element-4520">
                      {guardSummary.totalAffectedQuestions}
                    </p>
                    <p className="text-[10px] font-medium text-red-600" data-id="element-4521">
                      Vragen
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-2.5 border border-orange-100 text-center" data-id="element-4522">
                    <p className="text-lg font-bold text-orange-700" data-id="element-4523">
                      {guardSummary.totalRejections}
                    </p>
                    <p className="text-[10px] font-medium text-orange-600" data-id="element-4524">
                      Afwijzingen
                    </p>
                  </div>
                </div>
                {guardSummary.reasonBreakdown.length > 0 && <div className="space-y-1.5" data-id="element-4525">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-4526">
                      Top redenen
                    </p>
                    {guardSummary.reasonBreakdown.slice(0, 3).map(r => <div key={r.reason} className="flex items-start gap-2 text-[11px]" data-id="element-4527">
                        <span className="font-mono text-red-600 font-semibold flex-shrink-0" data-id="element-4528">
                          {r.count}×
                        </span>
                        <span className="text-gray-600 leading-tight" data-id="element-4529">
                          {r.description}
                        </span>
                      </div>)}
                  </div>}
                {guardSummary.issues.length > 0 && <div className="space-y-1.5" data-id="element-4530">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-4531">
                      Meest afgewezen
                    </p>
                    {guardSummary.issues.slice(0, 3).map(issue => <div key={issue.questionId} className="flex items-center justify-between text-[10px]" data-id="element-4532">
                        <span className="font-mono text-gray-700 truncate mr-2" title={issue.questionId} data-id="element-4533">
                          {issue.questionId}
                        </span>
                        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-4534">
                          {issue.fatalCount > 0 && <span className="text-red-600 font-semibold" data-id="element-4535">
                              {issue.fatalCount}F
                            </span>}
                          {issue.softCount > 0 && <span className="text-amber-600 font-semibold" data-id="element-4536">
                              {issue.softCount}S
                            </span>}
                        </div>
                      </div>)}
                  </div>}
                {guardSummary.dataRange && <p className="text-[10px] text-gray-400 pt-1 border-t border-gray-100" data-id="element-4537">
                    Data:{' '}
                    {new Date(guardSummary.dataRange.from).toLocaleDateString('nl-NL')}{' '}
                    —{' '}
                    {new Date(guardSummary.dataRange.to).toLocaleDateString('nl-NL')}
                  </p>}
              </div>}
          </Card>
        </div>
      </div>
    </div>;
}